/** Shared fact-register merge algebra; zero imports by design. */

export type FactRegisterStatus = "confirmed" | "conflict" | "missing";
export type FactRegisterConfidence = "high" | "medium" | "low";

export type FactRegisterDocument = {
	fileId: string;
	driveId?: string;
	name: string;
	webUrl?: string;
	eTag?: string;
};

export type FactRegisterRow = {
	key: string;
	category: string;
	label: string;
	value: string;
	status: FactRegisterStatus;
	confidence: FactRegisterConfidence;
	notes?: string;
	sourceDocuments: FactRegisterDocument[];
};

export type FactRegisterFacts = {
	facts: FactRegisterRow[];
	docxWebUrl?: string;
};

export type FactRegisterChanges = {
	category?: string;
	label?: string;
	value?: string;
	status?: FactRegisterStatus;
	confidence?: FactRegisterConfidence;
	notes?: string;
	sourceDocuments?: FactRegisterDocument[];
};

export type FactRegisterOperation =
	| { op: "add"; fact: FactRegisterRow }
	| { op: "update"; key: string; set: FactRegisterChanges }
	| { op: "remove"; key: string };

export type FactRegisterCommand =
	| {
			mode: "replace";
			baseVersion: number;
			facts: FactRegisterRow[];
			docxWebUrl?: string;
	  }
	| {
			mode: "patch";
			baseVersion: number;
			ops: FactRegisterOperation[];
			docxWebUrl?: string;
	  };

export type FactRegisterErrorCode =
	| "FACT_REGISTER_INVALID_ROW"
	| "FACT_REGISTER_INVALID_ROW_COUNT"
	| "FACT_REGISTER_DUPLICATE_KEY"
	| "FACT_REGISTER_UNKNOWN_KEY"
	| "FACT_REGISTER_EMPTY_OPS";

export type FactRegisterRowResult =
	| { ok: true; row: FactRegisterRow }
	| { ok: false; code: FactRegisterErrorCode; detail?: string };

export type FactRegisterApplyResult =
	| { ok: true; state: FactRegisterFacts }
	| { ok: false; code: FactRegisterErrorCode; detail?: string };

export const FACT_REGISTER_MAX_FACTS = 200;

function clean(value: string): string {
	return value.replace(/\s+/g, " ").trim();
}

function cleanDocument(document: FactRegisterDocument): FactRegisterDocument {
	return {
		fileId: clean(document.fileId),
		...(document.driveId ? { driveId: clean(document.driveId) } : {}),
		name: clean(document.name),
		...(document.webUrl ? { webUrl: document.webUrl.trim() } : {}),
		...(document.eTag ? { eTag: document.eTag.trim() } : {}),
	};
}

export function normalizeFactRow(
	input: FactRegisterRow,
): FactRegisterRowResult {
	const key = clean(input.key);
	const category = clean(input.category);
	const label = clean(input.label);
	if (!key || !category || !label) {
		return { ok: false, code: "FACT_REGISTER_INVALID_ROW", detail: key };
	}
	return {
		ok: true,
		row: {
			key,
			category,
			label,
			value: clean(input.value),
			status: input.status,
			confidence: input.confidence,
			...(input.notes?.trim() ? { notes: clean(input.notes) } : {}),
			sourceDocuments: input.sourceDocuments.map(cleanDocument),
		},
	};
}

/** Incoming link wins; absent link keeps the stored one. */
function nextState(
	facts: FactRegisterRow[],
	previousDocxWebUrl: string | undefined,
	incomingDocxWebUrl: string | undefined,
): FactRegisterFacts {
	const incoming = incomingDocxWebUrl?.trim();
	const docxWebUrl = incoming ? incoming : previousDocxWebUrl;
	return { facts, ...(docxWebUrl ? { docxWebUrl } : {}) };
}

function mergeRow(
	current: FactRegisterRow,
	set: FactRegisterChanges,
): FactRegisterRow {
	const notes = set.notes === undefined ? current.notes : set.notes;
	return {
		key: current.key,
		category: set.category ?? current.category,
		label: set.label ?? current.label,
		value: set.value ?? current.value,
		status: set.status ?? current.status,
		confidence: set.confidence ?? current.confidence,
		...(notes ? { notes } : {}),
		sourceDocuments: set.sourceDocuments ?? current.sourceDocuments,
	};
}

type FactRegisterOperationsResult =
	| { ok: true; facts: FactRegisterRow[] }
	| { ok: false; code: FactRegisterErrorCode; detail?: string };

function applyOperations(
	current: FactRegisterRow[],
	ops: FactRegisterOperation[],
): FactRegisterOperationsResult {
	const facts = [...current];
	for (const operation of ops) {
		if (operation.op === "add") {
			const normalized = normalizeFactRow(operation.fact);
			if (!normalized.ok) return normalized;
			if (facts.some((row) => row.key === normalized.row.key)) {
				return {
					ok: false,
					code: "FACT_REGISTER_DUPLICATE_KEY",
					detail: normalized.row.key,
				};
			}
			facts.push(normalized.row);
			continue;
		}
		const key = clean(operation.key);
		const index = facts.findIndex((row) => row.key === key);
		const existing = index < 0 ? undefined : facts[index];
		if (!existing) {
			return { ok: false, code: "FACT_REGISTER_UNKNOWN_KEY", detail: key };
		}
		if (operation.op === "remove") {
			facts.splice(index, 1);
			continue;
		}
		const merged = normalizeFactRow(mergeRow(existing, operation.set));
		if (!merged.ok) return merged;
		facts[index] = merged.row;
	}
	return { ok: true, facts };
}

/** Version checks live in the mutation, never here. */
export function applyFactRegisterCommand(
	state: FactRegisterFacts,
	command: FactRegisterCommand,
): FactRegisterApplyResult {
	if (command.mode === "replace") {
		if (
			command.facts.length === 0 ||
			command.facts.length > FACT_REGISTER_MAX_FACTS
		) {
			return { ok: false, code: "FACT_REGISTER_INVALID_ROW_COUNT" };
		}
		const facts: FactRegisterRow[] = [];
		const seen = new Set<string>();
		for (const input of command.facts) {
			const normalized = normalizeFactRow(input);
			if (!normalized.ok) return normalized;
			if (seen.has(normalized.row.key)) {
				return {
					ok: false,
					code: "FACT_REGISTER_DUPLICATE_KEY",
					detail: normalized.row.key,
				};
			}
			seen.add(normalized.row.key);
			facts.push(normalized.row);
		}
		return {
			ok: true,
			state: nextState(facts, state.docxWebUrl, command.docxWebUrl),
		};
	}
	if (command.ops.length === 0) {
		return { ok: false, code: "FACT_REGISTER_EMPTY_OPS" };
	}
	const applied = applyOperations(state.facts, command.ops);
	if (!applied.ok) return applied;
	if (
		applied.facts.length === 0 ||
		applied.facts.length > FACT_REGISTER_MAX_FACTS
	) {
		return { ok: false, code: "FACT_REGISTER_INVALID_ROW_COUNT" };
	}
	return {
		ok: true,
		state: nextState(applied.facts, state.docxWebUrl, command.docxWebUrl),
	};
}

/** Compact key/status list the model patches against. */
export function factRegisterManifest(
	facts: readonly FactRegisterRow[],
): string {
	return facts.map((row) => `${row.key}: ${row.status}`).join("\n");
}
